"use client";

import { cn } from "@oreme/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { Plus } from "lucide-react";
import Image from "next/image";
import { useLocale } from "next-intl";
import React, { type HTMLAttributes, type ReactElement } from "react";
import { LabelList, ObservatoryDrawer, ObservatoryTheme } from "@/components";
import { ThemeTypeEnum, Observatory } from "@/types";

const observatoryCardVariants = cva(
  "group relative flex flex-col flex-1 overflow-hidden bg-card transition pointer-events-auto min-h-[400px] hover:cursor-pointer hover:ring-1",
  {
    variants: {
      variant: {
        default:
          "rounded-lg border-0 transition hover:shadow-2xl hover:drop-shadow-2xl hover:-translate-y-2",
        static: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const observatoryCardFigureVariants = cva(
  "absolute inset-0 z-0 overflow-hidden transition-transform duration-10000 ease-in-out group-hover:scale-120",
);

interface ObservatoryCardProps
  extends
    HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof observatoryCardVariants> {
  readonly observatory: Observatory;
}

function ObservatoryCard({
  observatory,
  variant,
  className,
}: Readonly<ObservatoryCardProps>): ReactElement {
  const locale = useLocale();
  const theme = observatory.theme as ThemeTypeEnum;

  const name = observatory.name[locale];
  const description = observatory.description
    ? observatory.description[locale]
    : null;

  return (
    <div className={cn(observatoryCardVariants({ variant }), className)}>
      <figure className={cn(observatoryCardFigureVariants())}>
        <Image
          alt={name}
          src={observatory.picture!}
          sizes="(max-width: 768px) 100vw, 256px"
          fill
          className="object-cover"
          priority
        />
      </figure>
      {observatory.labels ? (
        <aside className="pointer-events-auto absolute top-0 left-0 right-8 p-2 text-sm leading-tight text-muted-foreground">
          <LabelList data={observatory.labels} />
        </aside>
      ) : null}
      <div className="absolute inset-0 z-10 pr-14 flex flex-col items-start justify-end gap-2 p-3 bg-linear-to-b from-transparent to-black/80">
        <ObservatoryTheme theme={theme} className="mb-2" />
        <h3 className="text-lg font-semibold leading-tight text-white">
          {name}
        </h3>
        {description ? (
          <p className="text-xs leading-tight text-white/70">{description}</p>
        ) : null}
      </div>
      <footer className="absolute right-0 bottom-0 z-10 flex flex-row justify-end p-3 pt-0 mt-auto">
        <span className="flex justify-center items-center w-8 h-8 rounded-full text-foreground border bg-background animate__animated animate__faster group-hover:animate__rotateIn">
          <Plus strokeWidth={1} />
        </span>
      </footer>
      <ObservatoryDrawer observatory={observatory} />
    </div>
  );
}

export { ObservatoryCard, type ObservatoryCardProps };
